import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { Box } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalytics, getDatabaseContainerType } from '@databases/analytics';
import { aiFirstCreationStreamingState } from '@databases/database-ai-first-creation-state';
import { useDatabaseStreamState } from '@databases/database-ai-first-creation-ui';
import {
  type DraggingItem,
  DragType,
  useDragItems,
  type HorizontalPositions,
} from '@databases/drag';
import {
  VERT_DRAG_HANDLER_ICON_WIDTH,
  DRAG_HANDLER_TD_ELEMENT_WIDTH,
} from '@databases/icons';
import Z_INDEX from '@databases/layout/z-index';
import { PluginKey } from '@databases/plugins';
import { isPluginEnabled } from '@databases/plugins/helper';
import { defaultStoreUtils } from '@databases/redux';
import { selectFieldSingle } from '@databases/redux/cellSelections/actions';
import {
  selectIsEntireEntrySelected,
  selectSelectedFieldIds,
} from '@databases/redux/cellSelections/selectors';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { selectDatabaseDraftByContentId } from '@databases/redux/changesets/selectors';
import { IGNORED_TABLE_ELEMENT_CLASS, RESIZABLE } from '@databases/table-resizer';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  NUMBERED_COLUMN_WIDTH,
  TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH,
  TABLE_CLASSNAMES,
} from '@databases/utils/constants';
import { getHiddenFields } from '@databases/utils/database/view/view';
import useContainerScrollPosition from '@databases/utils/hooks/useContainerScrollPosition';
import { collabDatabaseBatch } from '@databases/utils/redux/collab/batch';
import { batch } from '@databases/utils/redux/redux';
import useView from '@databases/views/useView';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useSnapshot } from 'valtio';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

import { type RefMapWithSizes, useRefMapWithSizes } from '../../../lazy-render';
import { shouldShowEntryPrefix } from '../../../utils';

import AddFieldButton from './AddFieldButton';
import { type EntryIsSelectedMap, EntryPrefixShadow } from './EntryPrefix';
import HeaderCheckbox from './HeaderCheckbox';
import TableLayoutHeaderCell, { Th } from './TableLayoutHeaderCell';
import { DRAG_ITEM, ADD_ITEM } from './TableLayoutRows';
import useHasNumberedEntries from './UseHasNumberedEntries';
import { useFadeGradient } from './hooks/useFadeGradient';

export interface TableLayoutHeaderRowProps {
  contentId: string;
  canEditContent: boolean;
  canEditFields: boolean;
  couldEditFields: boolean;
  containerLeft: number;
  containerHeight: number;
  tableBodyRef: React.MutableRefObject<HTMLTableSectionElement | null>;
  isConfluencePanelOpen: boolean;
}

const TR = styled.tr`
  z-index: ${Z_INDEX.TABLE_LAYOUT_HEADER_ROW};
  padding-top: ${token('space.100')};
`;

const StyledEntryPrefixTh = styled.th<{
  hasNumberedEntries: boolean;
  dragHandlerTdElementWidth: number;
  isVisualRefreshM1Enabled: boolean;
}>`
  width: ${(props) => (props.hasNumberedEntries ? NUMBERED_COLUMN_WIDTH : 0)}px;
  visibility: ${(props) => (props.hasNumberedEntries ? 'visible' : 'hidden')};
  left: ${(props) => props.dragHandlerTdElementWidth / 2}px;
  position: sticky;
  background: ${(props) =>
    props.isVisualRefreshM1Enabled
      ? `${token('elevation.surface.sunken')}`
      : `${token('elevation.surface', '#FFFFF')}`};
  z-index: ${Z_INDEX.TABLE_ROW};
  svg {
    width: 24px !important;
    height: 24px !important;
  }
`;

const TableLayoutHeaderRow = React.memo(
  React.forwardRef<HTMLTableRowElement | null, TableLayoutHeaderRowProps>(
    function TableLayoutHeaderRow(
      {
        contentId,
        canEditContent,
        canEditFields,
        couldEditFields,
        containerHeight,
        containerLeft,
        tableBodyRef,
        isConfluencePanelOpen,
      },
      ref
    ) {
      const { view } = useView();

      const containerScrollPosition = useContainerScrollPosition(
        'scrollable-inner-container'
      );

      const isVisualRefreshM1Enabled = isFeatureFlagEnabled(
        'databases-frontend-visual-refresh-table-m1'
      );
      const dragHandlerTdElementWidth = isVisualRefreshM1Enabled
        ? DRAG_HANDLER_TD_ELEMENT_WIDTH
        : VERT_DRAG_HANDLER_ICON_WIDTH;

      const { enableFadeGradient, getHorizontalFadeScrollStyles } = useFadeGradient({
        isConfluencePanelOpen,
      });

      const [fieldIds, isLocked, hiddenFields, selectedFieldIds, entryIsSelectedMap] =
        defaultStoreUtils.useEqualSelector((state) => {
          const databaseDraftView = selectDatabaseDraftByContentId(state, {
            contentId,
            viewSettings: view.settings,
            includeDeleted: true,
          });
          const fieldIds = databaseDraftView.fields;
          const entryIsSelectedMap: EntryIsSelectedMap = databaseDraftView.entries.reduce(
            (acc: EntryIsSelectedMap, entryId: string) => {
              acc[entryId] = selectIsEntireEntrySelected(state, {
                entryId,
              });
              return acc;
            },
            {} as EntryIsSelectedMap
          );

          return [
            fieldIds,
            databaseDraftView.meta.isLocked,
            getHiddenFields(view.settings.fields),
            selectSelectedFieldIds(state, { contentId }),
            entryIsSelectedMap,
          ] as const;
        });

      const prevFieldIdsRef = useRef(fieldIds);
      const [fieldToScroll, setFieldToScroll] = useState<string | null>(null);
      const { currentFieldOperation, fieldIdSelectedForProcessing } = useSnapshot(
        aiFirstCreationStreamingState
      );
      const { operationType, isStreaming } = useDatabaseStreamState();
      const isEditStreaming = isStreaming && operationType === 'edit';

      useEffect(() => {
        // check if new field has been added
        if (fieldIds.length > prevFieldIdsRef.current.length) {
          const newFieldIds = fieldIds.filter(
            (id) => !prevFieldIdsRef.current.includes(id)
          );
          if (newFieldIds.length > 0) {
            setFieldToScroll(newFieldIds[0]);
          }
        }
        prevFieldIdsRef.current = fieldIds;
      }, [fieldIds]);

      useEffect(() => {
        if (isEditStreaming && fieldIdSelectedForProcessing) {
          setFieldToScroll(fieldIdSelectedForProcessing);
        }
      }, [isEditStreaming, fieldIdSelectedForProcessing]);

      const showEntryPrefix = shouldShowEntryPrefix(view);
      const hasNumberedEntries = useHasNumberedEntries({ contentId }) && showEntryPrefix;
      const columnIds = useMemo(() => {
        const result = [...fieldIds];
        result.push(ADD_ITEM);

        return result;
      }, [fieldIds]);
      const columnRefMap: RefMapWithSizes<any> = useRefMapWithSizes({
        getPositions: ({ sizes }) => {
          const columnPositions: Record<string, HorizontalPositions> = {};
          let currentLeft = 0;
          for (const fieldId of columnIds) {
            const left = currentLeft;
            const width =
              fieldId === DRAG_ITEM
                ? 0
                : sizes[fieldId]?.width ?? TABLE_VIEW_SOFT_COLUMN_MIN_WIDTH;
            columnPositions[fieldId] = { left, width };
            currentLeft += width;
          }

          return columnPositions;
        },
      });

      useEffect(() => {
        if (!fieldToScroll) {
          return;
        }

        const container = document.getElementById('scrollable-inner-container');
        const position = columnRefMap.positions[fieldToScroll];

        if (!container || !position) {
          return;
        }

        requestAnimationFrame(() => {
          const containerRect = container.getBoundingClientRect();
          const containerWidth = containerRect.width;
          const currentScrollLeft = container.scrollLeft;

          const columnLeft = position.left;
          const columnWidth = position.width;
          const columnCenter = columnLeft + columnWidth / 2;

          const targetScrollLeft = columnCenter - containerWidth / 2;

          const maxScroll = container.scrollWidth - containerWidth;
          const clampedScrollLeft = Math.max(0, Math.min(targetScrollLeft, maxScroll));

          if (Math.abs(clampedScrollLeft - currentScrollLeft) > 1) {
            container.scrollTo({ left: clampedScrollLeft, behavior: 'smooth' });
          }

          setFieldToScroll(null);
        });
      }, [fieldToScroll, columnRefMap.positions, setFieldToScroll]);

      function extractIndex(data: Record<string, unknown>) {
        const { fieldIndex } = data;
        if (typeof fieldIndex !== 'number') {
          return null;
        }
        return fieldIndex;
      }

      const [draggingItem, setDraggingItem] = useState<DraggingItem>();

      const store = defaultStoreUtils.useStore();
      const dispatch = defaultStoreUtils.useDispatch();
      const client = useAnalytics();

      const deleteField =
        fieldIdSelectedForProcessing &&
        isEditStreaming &&
        currentFieldOperation === 'delete';

      useEffect(() => {
        if (deleteField) {
          requestAnimationFrame(() => {
            dispatch(
              selectFieldSingle({
                contentId,
                fieldId: fieldIdSelectedForProcessing,
              })
            );
            setFieldToScroll(fieldIdSelectedForProcessing);
          });
        }
      }, [
        fieldIdSelectedForProcessing,
        contentId,
        dispatch,
        isEditStreaming,
        setFieldToScroll,
        deleteField,
      ]);

      const onDrop = useCallback(
        (draggingItem: DraggingItem) => {
          if (!draggingItem) {
            return;
          }

          const { idx, ids } = draggingItem;
          if (!ids) {
            return;
          }
          batch(() => {
            collabDatabaseBatch(store, contentId, () => {
              // Reverse the fieldIds if dragging to the left to ensure
              // relative order is maintained for the selected fields
              const lowestSelectedFieldId = fieldIds.indexOf(ids[0]);
              const selectedFieldIds =
                lowestSelectedFieldId < idx ? ids : ids.slice().reverse();
              for (const fieldId of selectedFieldIds) {
                void dispatch(
                  addChangeToDraft(contentId, {
                    type: DatabaseChangeType.REORDER_FIELD,
                    fieldId: fieldId,
                    newIndex: idx,
                    hiddenFields,
                  })
                );
              }
            });
          });
          void client.sendColumnReorderedEvent({
            contentId: contentId,
            databaseContainer: getDatabaseContainerType(),
          });
        },
        [client, contentId, dispatch, hiddenFields, store, fieldIds]
      );

      const {
        handleDragLeave,
        handleDragEnter,
        handleDragEnd,
        handleDrag,
        handleDragOver,
      } = useDragItems({
        dragDirection: 'horizontal',
        ids: fieldIds,
        rowOrColIds: columnIds,
        contentId,
        positions: columnRefMap.positions,
        dragType: DragType.FIELD_COLUMNS,
        onDrop,
        onSetDraggingItem: setDraggingItem,
        topOrLeftPadding: 50 - containerLeft,
      });

      useEffect(() => {
        return combine(
          monitorForElements({
            canMonitor({ source }) {
              return source.data.type === DragType.FIELD_COLUMNS;
            },
            onDrop({ location, source }) {
              const destination = location.current.dropTargets[0];
              if (!destination) {
                return;
              }
              const startIndex = extractIndex(source.data);
              const indexOfTarget = extractIndex(destination.data);
              if (startIndex === null || indexOfTarget === null) {
                return;
              }
              const closestEdgeOfTarget = extractClosestEdge(destination.data);
              onDrop({
                // Special handling for last field
                idx:
                  indexOfTarget +
                  (indexOfTarget + 1 === fieldIds.length &&
                  closestEdgeOfTarget === 'right'
                    ? 1
                    : 0),
                ids: selectedFieldIds,
              });
            },
          })
        );
      }, [onDrop, fieldIds, selectedFieldIds]);

      const hideAddNewFieldButton = columnIds.length === 1 && columnIds[0] === 'add';

      const renderAddFieldsButton = () => {
        if (isVisualRefreshM1Enabled && hideAddNewFieldButton) {
          return null;
        }

        if (isLocked && couldEditFields) {
          return (
            <Tooltip content={() => 'Structure is locked'} position="top">
              <AddFieldButton contentId={contentId} currentViewId={view.id} isDisabled />
            </Tooltip>
          );
        } else if (canEditFields) {
          return <AddFieldButton contentId={contentId} currentViewId={view.id} />;
        }

        return null;
      };

      return (
        <TR
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          id="table-layout-header-row"
          data-testid="table-layout-header-row"
          ref={ref}
        >
          {canEditContent && showEntryPrefix && (
            <th
              className={IGNORED_TABLE_ELEMENT_CLASS}
              style={{
                position: 'sticky',
                width: `${dragHandlerTdElementWidth / 2}px`,
                background: token('elevation.surface', '#FFFFF'),
                left: 0,
                zIndex: Z_INDEX.ROW_SELECT_BUTTON,
              }}
            >
              {/* For visual refresh we are adding a border radius to table header row. Due to the way table header row is being rendered, we are able to apply border radius but
      we run into an overflow issue on scroll where the the underlying table cell borders remain visible and extend beyond the rounded corners during horizontal scrolling
      Usually an overflow: hidden will solve this problem, but because we have an extra th element at the beginning and end of the row with a position of sticky this causes unwanted issues
      To resolve, we use clip-path to hide the extended table rows for now and we can revisit if we refactor our table layout
      */}
              {isVisualRefreshM1Enabled && (
                <>
                  <div
                    style={{
                      width: token('space.100'),
                      height: token('space.100'),
                      backgroundColor: token('elevation.surface', '#FFFFFF'),
                      position: 'absolute',
                      top: '0',
                      right: token('space.negative.075', '-6px'),
                      clipPath: `path('M 0 8 L 0 0 L 8 0 C 8 0, 0 1, 0 12 Z')`,
                    }}
                  />
                  <div
                    style={{
                      width: token('space.100'),
                      height: token('space.100'),
                      backgroundColor: token('elevation.surface', '#FFFFFF'),
                      position: 'absolute',
                      bottom: '0',
                      right: token('space.negative.075', '-6px'),
                      clipPath: `path('M 0 0 L 0 8 L 8 8 C 8 8, 0 7, 0 0 Z')`,
                    }}
                  />
                </>
              )}
            </th>
          )}
          <StyledEntryPrefixTh
            className="entry-number-table-header"
            hasNumberedEntries={hasNumberedEntries}
            dragHandlerTdElementWidth={dragHandlerTdElementWidth}
            isVisualRefreshM1Enabled={isVisualRefreshM1Enabled}
          >
            {isVisualRefreshM1Enabled && (
              <>
                <div
                  style={{
                    width: token('space.100'),
                    height: token('space.100'),
                    backgroundColor: token('elevation.surface', '#FFFFFF'),
                    position: 'absolute',
                    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                    top: '-1px',
                    left: token('space.negative.025', '-2px'),
                    clipPath: `path('M 0 8 L 0 0 L 8 0 C 8 0, 0 1, 0 12 Z')`,
                  }}
                />
                <div
                  style={{
                    width: token('space.100'),
                    height: token('space.100'),
                    backgroundColor: token('elevation.surface', '#FFFFFF'),
                    position: 'absolute',
                    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                    bottom: '-1px',
                    // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
                    right: '41px',
                    clipPath: `path('M 0 0 L 0 8 L 8 8 C 8 8, 0 7, 0 0 Z')`,
                  }}
                />
              </>
            )}
            {isVisualRefreshM1Enabled && isPluginEnabled(PluginKey.CELL_SELECTION) && (
              <HeaderCheckbox
                entryIsSelectedMap={entryIsSelectedMap}
                contentId={contentId}
              />
            )}
            {isVisualRefreshM1Enabled ? null : (
              <EntryPrefixShadow
                style={{ display: containerScrollPosition > 0 ? 'block' : 'none' }}
              />
            )}
          </StyledEntryPrefixTh>
          {columnIds.map((fieldId) =>
            fieldId !== DRAG_ITEM && fieldId !== ADD_ITEM ? (
              <TableLayoutHeaderCell
                key={fieldId}
                canEditFields={canEditFields}
                couldEditFields={couldEditFields}
                contentId={contentId}
                fieldId={fieldId}
                currentViewId={view.id}
                className={RESIZABLE}
                ref={{
                  current: {
                    tableBodyRef: tableBodyRef,
                    headerFieldRef: columnRefMap.refs[fieldId],
                  },
                }}
                isFirstInRow={fieldIds[0] === fieldId}
                isLastInRow={fieldIds[fieldIds.length - 1] === fieldId}
                onDrag={handleDrag}
                onDragEnd={handleDragEnd}
                isDraggingActive={!!draggingItem}
                containerHeight={containerHeight}
                isDragOver={false}
                fieldIndex={columnIds.indexOf(fieldId)}
                fieldToScroll={fieldToScroll}
                setFieldToScroll={setFieldToScroll}
              />
            ) : (
              fieldId !== DRAG_ITEM &&
              fieldId === ADD_ITEM && (
                <Th
                  key={fieldId}
                  className={classNames([TABLE_CLASSNAMES.ADD_FIELD], {
                    [IGNORED_TABLE_ELEMENT_CLASS]: true,
                  })}
                  containerHeight={containerHeight}
                  isDragOver={false}
                >
                  {isVisualRefreshM1Enabled && enableFadeGradient && (
                    <Box
                      xcss={getHorizontalFadeScrollStyles() as any}
                      style={{ zIndex: Z_INDEX.SCROLL_FADE_GRADIENT }}
                    />
                  )}
                  {renderAddFieldsButton()}
                </Th>
              )
            )
          )}
        </TR>
      );
    }
  )
);

export default TableLayoutHeaderRow;
